// Ported from Waffy-web-next (src/setup/util/phoneRuleConfig.helpers.ts) so both apps validate
// phone numbers against the same backend-driven rules.
import {
  EOtpMethod,
  IPhoneRuleConfig,
} from "../api/services/phoneRuleConfigs/models";
import { configureNumber } from "./helpers";

export const SAUDI_COUNTRY_CODE = "966";

/**
 * Saudi Arabia default config. Used while the phone-rule-configs API is
 * loading and as a fallback when the request fails.
 */
export const SA_FALLBACK_CONFIG: IPhoneRuleConfig = {
  countryCode: SAUDI_COUNTRY_CODE,
  countryName: "Saudi Arabia",
  countryNameAr: "السعودية",
  countryFlag: "🇸🇦",
  enabled: true,
  nationalNumberLength: 9,
  nationalNumberLengthWithZero: 10,
  hasExtraZeroPrefix: true,
  allowedPrefixes: ["5"],
  defaultOtpMethod: EOtpMethod.WHATSAPP,
  resendOtpMethods: [EOtpMethod.WHATSAPP],
};

/**
 * Country name to display. Uses the Arabic name when the UI language is Arabic,
 * falling back to the English name when the Arabic name is not available.
 */
export const getCountryDisplayName = (
  country: IPhoneRuleConfig,
  language?: string
): string => {
  const isArabic = !!language && language.toLowerCase().startsWith("ar");
  if (isArabic) return country.countryNameAr || country.countryName;
  return country.countryName;
};

/**
 * Convert Arabic-Indic digits to Western digits and strip anything that is not
 * a digit, so downstream logic always works with a clean numeric string.
 */
export const sanitizePhoneDigits = (value: string): string =>
  `${configureNumber(value ?? "")}`.replace(/\D/g, "");

/**
 * Whether the entered national number carries the optional leading-zero prefix
 * that some countries allow (e.g. 0512345678 for KSA).
 */
export const hasLeadingZero = (
  nationalNumber: string,
  config: IPhoneRuleConfig
): boolean =>
  config.hasExtraZeroPrefix &&
  !!config.nationalNumberLengthWithZero &&
  nationalNumber.startsWith("0");

/**
 * The significant part of the number, i.e. the national number without the
 * optional leading zero. This is what allowed-prefixes are checked against and
 * what gets sent (after the country code) to the backend.
 */
export const getSignificantNumber = (
  nationalNumber: string,
  config: IPhoneRuleConfig
): string =>
  hasLeadingZero(nationalNumber, config)
    ? nationalNumber.slice(1)
    : nationalNumber;

/**
 * Max number of digits the input should accept for a given country. When a
 * country supports the extra leading zero we allow the longer (with-zero)
 * length so the user can type it.
 */
export const getMaxInputLength = (config: IPhoneRuleConfig): number =>
  config.hasExtraZeroPrefix && config.nationalNumberLengthWithZero
    ? config.nationalNumberLengthWithZero
    : config.nationalNumberLength;

const getExpectedLength = (
  nationalNumber: string,
  config: IPhoneRuleConfig
): number =>
  hasLeadingZero(nationalNumber, config)
    ? (config.nationalNumberLengthWithZero as number)
    : config.nationalNumberLength;

/**
 * Whether enough digits have been typed to judge the number as complete, so
 * callers can hold off showing a validation error while the user is still typing.
 */
export const isPhoneNumberComplete = (
  rawNationalNumber: string,
  config: IPhoneRuleConfig
): boolean => {
  const nationalNumber = sanitizePhoneDigits(rawNationalNumber);
  if (!nationalNumber) return false;
  return nationalNumber.length >= getExpectedLength(nationalNumber, config);
};

const isLengthValid = (
  nationalNumber: string,
  config: IPhoneRuleConfig
): boolean => {
  if (nationalNumber.startsWith("0")) {
    // Leading zero only permitted when the country supports it.
    if (!config.hasExtraZeroPrefix || !config.nationalNumberLengthWithZero) {
      return false;
    }
    return nationalNumber.length === config.nationalNumberLengthWithZero;
  }
  return nationalNumber.length === config.nationalNumberLength;
};

const isPrefixValid = (
  nationalNumber: string,
  config: IPhoneRuleConfig
): boolean => {
  const significant = getSignificantNumber(nationalNumber, config);
  if (!config.allowedPrefixes?.length) return true;
  return config.allowedPrefixes.some((prefix) =>
    significant.startsWith(prefix)
  );
};

/**
 * Whether the digits typed so far can no longer lead to any allowed prefix, no
 * matter what's typed next. A partial prefix that could still complete into a
 * match is not considered invalid, so the error shows immediately for a wrong
 * prefix while otherwise waiting for the full length.
 */
export const isPrefixDefinitivelyInvalid = (
  rawNationalNumber: string,
  config: IPhoneRuleConfig
): boolean => {
  const nationalNumber = sanitizePhoneDigits(rawNationalNumber);
  if (!nationalNumber) return false;
  const significant = getSignificantNumber(nationalNumber, config);
  if (!config.allowedPrefixes?.length || !significant) return false;
  const canStillMatch = config.allowedPrefixes.some(
    (prefix) => prefix.startsWith(significant) || significant.startsWith(prefix)
  );
  return !canStillMatch;
};

/** Length (with/without zero) and allowed prefixes, as defined by the backend. */
export const isPhoneNumberValid = (
  rawNationalNumber: string,
  config: IPhoneRuleConfig
): boolean => {
  const nationalNumber = sanitizePhoneDigits(rawNationalNumber);
  if (!nationalNumber) return false;
  return (
    isLengthValid(nationalNumber, config) &&
    isPrefixValid(nationalNumber, config)
  );
};

/**
 * The E.164 number stored on a party: "+" + country code + the national number
 * with any leading zero stripped (e.g. "+966512345678", "+201150727820").
 */
export const buildFullPhoneNumber = (
  rawNationalNumber: string,
  config: IPhoneRuleConfig
): string => {
  const nationalNumber = sanitizePhoneDigits(rawNationalNumber);
  const significant = getSignificantNumber(nationalNumber, config);
  return `+${config.countryCode}${significant}`;
};

/**
 * Finds the country a full number belongs to by its longest matching dial code,
 * e.g. "+971501234567" → UAE rather than a shorter code that happens to prefix it.
 */
export const findCountryByPhoneNumber = (
  phoneNumber: string,
  countries: IPhoneRuleConfig[]
): IPhoneRuleConfig | undefined => {
  const digits = sanitizePhoneDigits(phoneNumber);
  return countries
    .filter((country) => digits.startsWith(country.countryCode))
    .sort((a, b) => b.countryCode.length - a.countryCode.length)[0];
};

/**
 * Splits a stored party number back into the picker's country + national digits.
 * Numbers without a "+" predate the country picker and are always Saudi.
 */
export const splitPhoneNumber = (
  phoneNumber: string | undefined,
  countries: IPhoneRuleConfig[]
): { country: IPhoneRuleConfig; nationalNumber: string } => {
  const saudi =
    countries.find((c) => c.countryCode === SAUDI_COUNTRY_CODE) ||
    SA_FALLBACK_CONFIG;
  const raw = `${phoneNumber ?? ""}`.replace(/\s/g, "");
  if (!raw) return { country: saudi, nationalNumber: "" };

  if (!raw.startsWith("+")) {
    return { country: saudi, nationalNumber: sanitizePhoneDigits(raw) };
  }

  const country = findCountryByPhoneNumber(raw, countries) || saudi;
  const digits = sanitizePhoneDigits(raw);
  return {
    country,
    nationalNumber: digits.startsWith(country.countryCode)
      ? digits.slice(country.countryCode.length)
      : digits,
  };
};
